import React, { useEffect, useState } from "react";
import { adminApi } from "./adminApi";

function StatCard({ label, value, sub, testid }) {
    return (
        <div data-testid={testid} className="border border-burgundy/15 bg-cream p-6">
            <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">{label}</div>
            <div className="mt-2 font-display text-4xl text-burgundy leading-none">{value}</div>
            {sub && <div className="mt-2 text-xs text-ink-muted">{sub}</div>}
        </div>
    );
}

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [archetypes, setArchetypes] = useState([]);

    useEffect(() => {
        (async () => {
            const s = await adminApi.stats();
            setStats(s);
            const arch = await adminApi.archetypes();
            setArchetypes(arch);
        })();
    }, []);

    if (!stats) return <div className="p-10 text-ink-muted">Loading…</div>;

    const archMap = Object.fromEntries(archetypes.map(a => [a.id, a]));

    return (
        <div className="p-10 max-w-6xl" data-testid="admin-overview">
            <div className="flex items-end justify-between">
                <div>
                    <span className="text-[10px] tracking-[0.36em] uppercase text-gold-deep">Overview</span>
                    <h1 className="mt-2 font-display text-5xl text-burgundy">
                        <span className="italic">Today's</span> ledger
                    </h1>
                </div>
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard testid="stat-total-users" label="Total users" value={stats.total_users} sub={`${stats.recent_signups_7d} new in last 7d`} />
                <StatCard testid="stat-completed" label="Completed assessment" value={stats.completed_quiz} />
                <StatCard testid="stat-paid" label="Paid unlocks" value={stats.paid_unlocks} sub="Mocked checkout" />
                <StatCard testid="stat-referral" label="Referral unlocks" value={stats.referral_unlocks} />
            </div>

            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard testid="stat-locked" label="Still locked" value={stats.locked} />
                <StatCard testid="stat-disqualified" label="Disqualified" value={stats.disqualified ?? 0} sub="Screening or bias-check" />
                <StatCard testid="stat-emails" label="Emails captured" value={stats.emails_captured ?? 0} sub="Pre-paywall lifecycle" />
                <StatCard testid="stat-conversion" label="Conversion rate" value={`${stats.conversion_rate}%`} sub="paid + referral" />
            </div>

            {/* Archetype distribution */}
            <div className="mt-12">
                <span className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">Distribution</span>
                <h2 className="mt-2 font-display text-3xl text-burgundy">
                    <span className="italic">Who</span> are they?
                </h2>

                <div className="mt-6 border border-burgundy/15 bg-cream">
                    {stats.archetype_distribution.length === 0 ? (
                        <div className="px-6 py-10 text-center text-ink-muted text-sm">
                            No completed assessments yet.
                        </div>
                    ) : stats.archetype_distribution.map((d) => {
                        const arch = archMap[d.archetype_id];
                        const max = Math.max(...stats.archetype_distribution.map(x => x.count));
                        const pct = (d.count / max) * 100;
                        return (
                            <div
                                key={d.archetype_id}
                                className="flex items-center justify-between px-6 py-4 border-b last:border-b-0 border-burgundy/10"
                                data-testid={`dist-${d.archetype_id}`}
                            >
                                <div className="flex-1">
                                    <div className="font-display text-lg text-burgundy">
                                        {arch?.name || d.archetype_id}
                                    </div>
                                    <div className="mt-2 h-1 w-full bg-burgundy/10">
                                        <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
                                    </div>
                                </div>
                                <div className="ml-6 text-right">
                                    <div className="font-display text-2xl text-burgundy">{d.count}</div>
                                    <div className="text-[10px] tracking-[0.28em] uppercase text-ink-muted">
                                        users
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
